import {
  withValidation,
  composeSDKFactories,
  createElementPropsSDKFactory,
  changePropsSDKFactory,
  clickPropsSDKFactory,
  childrenPropsSDKFactory,
  createAccessibilityPropSDKFactory,
  focusPropsSDKFactory,
  assert,
  toJSONBase,
  registerCorvidEvent,
} from '@wix/editor-elements-corvid-utils';
import type { $W } from '@wix/thunderbolt-symbols';
import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';
import type { ISingleTabSDK } from '../../SingleTab/SingleTab.types';
import type {
  ITabsProps,
  ITabsSDK,
  ITabsOwnSDKFactory,
  OnTabItemClickedComponentEvent,
  OnTabItemClickedCorvidEvent,
} from '../Tabs.types';
import { customValidators, validationSchema } from './Tabs.validations';

const getIdToTabMap = (tabs: Array<ISingleTabSDK>) => {
  const idToTabMap: Record<string, ISingleTabSDK> = {};
  for (const tab of tabs) {
    idToTabMap[tab.id] = tab;
  }
  return idToTabMap;
};

const tabsSDKFactory: ITabsOwnSDKFactory = api => {
  const { props, setProps, getChildren, metaData } = api;

  const sdkProps: ReturnType<ITabsOwnSDKFactory> = {
    _setTabLabel(tabId, newLabel) {
      const { tabItems } = props;
      const updatedTabItems = [...tabItems];
      const tabIndex = tabItems.findIndex(item => item.id === tabId);
      updatedTabItems[tabIndex] = {
        ...updatedTabItems[tabIndex],
        label: newLabel,
      };

      setProps({ tabItems: updatedTabItems });
    },
    get tabs() {
      const { tabItems } = props;
      const tabsAsChildren = getChildren();

      const sortedAsEditorTabs = tabItems.map(tabItem =>
        tabsAsChildren.find(tab => tab.uniqueId === tabItem.id),
      );

      return sortedAsEditorTabs;
    },
    get currentTab() {
      const { tabItems, currentTabId } = props;
      const uniqueId = tabItems.find(({ tabId }) => tabId === currentTabId)?.id;

      return this.tabs.find(
        (singleTab: ISingleTabSDK) => singleTab.uniqueId === uniqueId,
      )!;
    },
    get defaultTab() {
      const { tabItems, defaultTabId } = props;
      const uniqueId = tabItems.find(({ tabId }) => tabId === defaultTabId)?.id;

      return this.tabs.find(
        (singleTab: ISingleTabSDK) => singleTab.uniqueId === uniqueId,
      )!;
    },
    orderTabs(tabReferences: Array<string> | Array<ISingleTabSDK>) {
      const { tabItems } = props;
      const tabs = getChildren();
      const idToTabMap = getIdToTabMap(tabs);

      const sortedTabs = tabReferences.map(tabReference => {
        const tab = assert.isString(tabReference)
          ? idToTabMap[tabReference]
          : tabReference;
        return tabItems.find(item => item.id === tab.uniqueId);
      });

      setProps({ tabItems: sortedTabs });
      return Promise.resolve();
    },
    changeTab(tabReference: string | ISingleTabSDK) {
      const tabs = getChildren();
      const { tabItems, currentTabId } = props;
      const idToTabMap = getIdToTabMap(tabs);

      const nextTab = assert.isString(tabReference)
        ? idToTabMap[tabReference]
        : tabReference;

      const nextTabId = tabItems.find(
        ({ id }) => id === nextTab.uniqueId,
      )?.tabId;
      if (nextTabId !== currentTabId) {
        setProps({ currentTabId: nextTabId });
      }
      return Promise.resolve(nextTab);
    },
    onTabItemClicked: (
      cb: (event: OnTabItemClickedCorvidEvent, $w: $W) => void,
    ) =>
      registerCorvidEvent<
        OnTabItemClickedComponentEvent,
        OnTabItemClickedCorvidEvent
      >(
        'onTabItemClicked',
        api,
        cb,
        ({ componentEvent: { tabId: uniqueId, ...componentEvent } }) => {
          const { tabs } = sdkProps;
          // Return Velo tab id instead of compId
          const tabId = tabs.find(
            (singleTab: ISingleTabSDK) => singleTab.uniqueId === uniqueId,
          )!.id;
          return {
            ...componentEvent,
            tabId,
          };
        },
      ),
    toJSON() {
      const { tabs, currentTab, defaultTab } = sdkProps;
      return {
        ...toJSONBase(metaData),
        tabs,
        currentTab,
        defaultTab,
      };
    },
  };
  return sdkProps;
};

const tabs = withValidation(tabsSDKFactory, validationSchema, customValidators);
export const accessibilityPropsSDKFactory = createAccessibilityPropSDKFactory({
  enableAriaLabel: true,
  enableAriaDescribedBy: false,
  enableAriaLabelledBy: true,
  enableTabIndex: true,
});

export const sdk = composeSDKFactories<ITabsProps, ITabsSDK>([
  createElementPropsSDKFactory(),
  changePropsSDKFactory,
  clickPropsSDKFactory,
  childrenPropsSDKFactory,
  focusPropsSDKFactory,
  accessibilityPropsSDKFactory,
  tabs,
]);

export default createComponentSDKModel(sdk);
